import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { doc, onSnapshot } from 'firebase/firestore'

import {
  CORPORATE_ENTITLEMENTS_QUERY_KEY,
  SITE_ENTITLEMENTS_QUERY_KEY,
} from '@hapana/shared/web/hooks/entitlements.hooks'

import { db } from '../config/firebase.config'
import { useTenantConfig } from './use-tenant-config'

const SIGNAL_COLLECTION = 'entitlementSignals'
const RETRY_DELAY_MS = 4000
const MAX_RETRIES = 5
const ENTITLEMENT_QUERY_KEYS = [CORPORATE_ENTITLEMENTS_QUERY_KEY, SITE_ENTITLEMENTS_QUERY_KEY]

/**
 * Keeps this brand's entitlements live. The backend stamps `entitlementSignals/{staffTenantId}` whenever
 * the brand's tier, themes or the published catalog change; this drops the cached entitlement queries
 * when that doc moves, so gated screens re-read the API within seconds instead of at the end of the
 * five-minute cache.
 *
 * The doc carries no entitlement data, so the first snapshot is only a baseline: refetching on it
 * would double every page load. Only a change after that baseline invalidates, or a baseline stamped
 * after the cached entitlements were fetched, which is a change that landed before the listener
 * attached and would otherwise go unseen until the cache expires.
 *
 * The doc is keyed by the brand's staff tenant, which is also what the Security Rule checks against
 * the signed-in user's token. A listener that errors is dead, so a failed read is retried a few times
 * (a session whose token has not settled yet is the usual cause).
 */
export const useEntitlementsSignal = (enabled: boolean) => {
  const queryClient = useQueryClient()
  const { data } = useTenantConfig()
  const staffTenantId: string | undefined = data?.tenants?.staff ?? undefined

  useEffect(() => {
    if (!enabled || !staffTenantId) {
      return undefined
    }

    let baseline: number | null = null
    let unsubscribe: (() => void) | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let retries = 0
    let cancelled = false

    const invalidateEntitlements = () => {
      ENTITLEMENT_QUERY_KEYS.forEach((key) => queryClient.invalidateQueries(key))
    }

    const hasEntitlementsFetchedBefore = (changedAtMs: number) =>
      ENTITLEMENT_QUERY_KEYS.some((key) =>
        queryClient
          .getQueryCache()
          .findAll(key)
          .some(({ state }) => state.dataUpdatedAt > 0 && state.dataUpdatedAt < changedAtMs)
      )

    const subscribe = () => {
      unsubscribe = onSnapshot(
        doc(db, SIGNAL_COLLECTION, staffTenantId),
        (snapshot) => {
          retries = 0
          const signal = snapshot.exists() ? snapshot.data() : undefined
          const sequence = Number(signal?.sequence ?? 0)
          if (baseline === null) {
            baseline = sequence
            const changedAtMs: number | undefined = signal?.lastUpdated?.toMillis?.()
            if (changedAtMs !== undefined && hasEntitlementsFetchedBefore(changedAtMs)) {
              invalidateEntitlements()
            }
            return
          }
          if (sequence === baseline) {
            return
          }
          baseline = sequence
          invalidateEntitlements()
        },
        (error) => {
          // Never block the app on the nudge: without it, entitlements still refresh on the
          // cache's own expiry.
          if (!cancelled && retries < MAX_RETRIES) {
            retries += 1
            retryTimer = setTimeout(subscribe, RETRY_DELAY_MS)
            return
          }
          console.warn('[entitlements-signal] listener unavailable', error.code)
        }
      )
    }

    subscribe()

    return () => {
      cancelled = true
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
      unsubscribe?.()
    }
  }, [enabled, staffTenantId, queryClient])
}
